import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Hash, Save, Info } from "lucide-react";

const DEFAULTS = { format: "{prefix}-{year}-{seq}", seq_digits: 5, default_prefix: "PCI", counter_scope: "cliente" };

const FORMATS = [
  { value: "{prefix}-{year}-{seq}", label: "PREFIXO-ANO-NÚMERO (ex.: EST-2026-00001)" },
  { value: "{prefix}-{client}-{year}-{seq}", label: "PREFIXO-CLIENTE-ANO-NÚMERO (ex.: EST-FS-2026-00001)" },
  { value: "{client}-{prefix}-{year}-{seq}", label: "CLIENTE-PREFIXO-ANO-NÚMERO (ex.: FS-EST-2026-00001)" },
  { value: "{prefix}-{seq}", label: "PREFIXO-NÚMERO (ex.: EST-00001)" },
];

export function ProtocolConfigPanel({ categories = [], types = [], onChanged }) {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [prefixes, setPrefixes] = useState({});
  const [clients, setClients] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/taxonomy/protocol-config");
      setCfg({ ...DEFAULTS, ...data });
    } catch (e) { /* mantém padrões */ }
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setPrefixes(Object.fromEntries(categories.map((c) => [c.id, c.protocol_prefix || ""])));
  }, [categories]);

  const saveCfg = async () => {
    setBusy(true);
    try {
      await api.put("/taxonomy/protocol-config", { ...cfg, seq_digits: Number(cfg.seq_digits) || 5 });
      toast.success("Parâmetros de protocolo atualizados — vale para os novos registros");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const savePrefix = async (cat) => {
    const value = (prefixes[cat.id] || "").toUpperCase();
    try {
      await api.put(`/taxonomy/categories/${cat.id}`, { name: cat.name, classes: cat.classes || ["cliente"], protocol_prefix: value });
      toast.success(`Prefixo de "${cat.name}" salvo`);
      onChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sample = (prefix) => {
    const year = new Date().getFullYear();
    const code = clients[0]?.code || "CLI";
    return cfg.format
      .replace("{prefix}", prefix || cfg.default_prefix || "PCI")
      .replace("{client}", code)
      .replace("{year}", String(year))
      .replace("{seq}", "1".padStart(Number(cfg.seq_digits) || 5, "0"));
  };

  return (
    <Card className="p-5 border-border bg-card space-y-5" data-testid="protocol-config-panel">
      <div>
        <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
          <Hash size={15} className="text-primary" /> Protocolos e Código de Rastreio
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          O prefixo é definido pelo tipo/classificação da ocorrência e a sequência numérica é independente por cliente.
          Números nunca são reutilizados, mesmo após exclusão. Protocolos já emitidos permanecem intactos.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Formato do protocolo</Label>
          <Select value={cfg.format} onValueChange={(v) => setCfg({ ...cfg, format: v })}>
            <SelectTrigger data-testid="protocol-format-select"><SelectValue /></SelectTrigger>
            <SelectContent>{FORMATS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Dígitos do número</Label>
          <Input type="number" min={3} max={8} value={cfg.seq_digits}
            onChange={(e) => setCfg({ ...cfg, seq_digits: e.target.value })} data-testid="protocol-digits-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Prefixo padrão (sem categoria)</Label>
          <Input value={cfg.default_prefix} onChange={(e) => setCfg({ ...cfg, default_prefix: e.target.value.toUpperCase() })}
            data-testid="protocol-default-prefix-input" />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Contador</Label>
          <Select value={cfg.counter_scope} onValueChange={(v) => setCfg({ ...cfg, counter_scope: v })}>
            <SelectTrigger data-testid="protocol-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cliente">Independente por cliente (recomendado)</SelectItem>
              <SelectItem value="global">Único para toda a plataforma</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2 flex items-end">
          <div className="rounded-md border border-border bg-background px-3 py-2 w-full">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Exemplo</p>
            <p className="font-mono text-sm" data-testid="protocol-sample">{sample("EST")}</p>
          </div>
        </div>
      </div>

      {cfg.counter_scope === "cliente" && !cfg.format.includes("{client}") && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 flex gap-2 text-xs" data-testid="protocol-uniqueness-note">
          <Info size={14} className="text-amber-500 shrink-0 mt-0.5" />
          <span>
            Com contador por cliente e sem o identificador do cliente no formato, o sistema pula automaticamente os
            números já usados por outro cliente para manter o protocolo único e rastreável. Para sequências totalmente
            independentes, escolha um formato que inclua o cliente.
          </span>
        </div>
      )}

      <Button onClick={saveCfg} disabled={busy} data-testid="protocol-save-btn">
        <Save size={14} className="mr-2" /> Salvar parâmetros de protocolo
      </Button>

      <div className="space-y-2 pt-2 border-t border-border">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Prefixo por categoria</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {categories.map((c) => (
            <div key={c.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-2" data-testid={`protocol-prefix-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{c.name}</p>
                <p className="text-[10px] font-mono text-muted-foreground">{sample(prefixes[c.id])}</p>
              </div>
              <Input className="w-24 h-8 uppercase font-mono text-xs" maxLength={8}
                value={prefixes[c.id] || ""}
                onChange={(e) => setPrefixes({ ...prefixes, [c.id]: e.target.value.toUpperCase() })}
                data-testid={`protocol-prefix-input-${c.id}`} />
              <Button size="sm" variant="outline" className="h-8" onClick={() => savePrefix(c)} data-testid={`protocol-prefix-save-${c.id}`}>Salvar</Button>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Cada tipo de ocorrência também aceita um prefixo próprio (editando o tipo) — quando preenchido, ele vence o da categoria.
        </p>
      </div>

      <div className="pt-2 border-t border-border">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Código dos clientes</p>
        <div className="flex flex-wrap gap-1.5">
          {clients.map((c) => (
            <Badge key={c.id} variant="outline" className="font-mono text-[10px]" data-testid={`protocol-client-code-${c.id}`}>
              {c.code || "—"} · {c.name}
            </Badge>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">O código curto de cada cliente é editado em Cadastros → Cliente.</p>
      </div>
    </Card>
  );
}

export default ProtocolConfigPanel;
